/*
 * PENDING INVITES on Get ready (boardroom go 2026-10-08, prod finding).
 *
 * An invitee whose ticket was allocated to their email but who signs in
 * WITHOUT the emailed /claim link had no way to see or accept it: the holdings
 * read only returns CLAIMED seats. This section lists the account's unclaimed
 * invites (GET /me/pending-invites — keyed server-side on the VERIFIED email,
 * never a token) as "Accept your ticket" cards; Accept calls POST
 * /me/invites/{holderId}/accept (the SAME server claim as the /claim link),
 * then signals the live Get-ready subscription to re-read holdings and the
 * screen navigates STRAIGHT to the accepted event's Get-ready view
 * (/get-ready?event=<eventId> — boardroom ruling (c) on PR #311), which relies
 * on its own load rather than waiting here.
 *
 * NEVER BLOCKS Get ready: the section is hidden while empty and on ANY read
 * failure; an accept failure shows neutral copy on that card only.
 */
import { useCallback, useEffect, useState } from 'react'
import { loadSession } from '../auth/session'
import { bumpReadinessRefresh } from '../data/getReady'
import { recordGuardianClaim, removeGuardianClaim } from '../data/guardianClaim'
import { acceptPendingInvite, fetchPendingInvites, myApiBase, type PendingInvite } from '../data/myApi'
import { useLocale } from '../i18n/useLocale'

export interface PendingInvitesState {
  /** 'loading' until the first read settles (success OR failure). */
  status: 'loading' | 'done'
  /** [] on any read failure — the section then simply does not render. */
  invites: PendingInvite[]
  /** Drop one invite locally (just accepted) and re-read the list. */
  dismissAndReload: (holderId: string) => void
}

export function usePendingInvites(): PendingInvitesState {
  const [state, setState] = useState<{ status: 'loading' | 'done'; invites: PendingInvite[] }>({ status: 'loading', invites: [] })
  const [tick, setTick] = useState(0)
  const dismissAndReload = useCallback((holderId: string) => {
    setState((s) => ({ ...s, invites: s.invites.filter((i) => i.holderId !== holderId) }))
    setTick((n) => n + 1)
  }, [])

  useEffect(() => {
    const base = myApiBase()
    const session = loadSession()
    if (!base || !session) {
      setState({ status: 'done', invites: [] })
      return
    }
    let cancelled = false
    fetchPendingInvites(session.idToken, base)
      .then((invites) => { if (!cancelled) setState({ status: 'done', invites }) })
      .catch(() => { if (!cancelled) setState({ status: 'done', invites: [] }) })
    return () => { cancelled = true }
  }, [tick])

  return { ...state, dismissAndReload }
}

export function PendingInvitesSection({ invites, onAccepted }: {
  invites: PendingInvite[]
  /** Called after a successful accept with the accepted seat's ids — the
   *  screen navigates straight to that event's Get-ready view (ruling c). */
  onAccepted: (accepted: { holderId: string; eventId: string }) => void
}) {
  if (invites.length === 0) return null
  return (
    <div data-testid="getready-pending-invites" style={{ maxWidth: 'var(--content-max)' }}>
      {invites.map((inv) => (
        <PendingInviteCard key={inv.holderId} invite={inv} onAccepted={onAccepted} />
      ))}
    </div>
  )
}

function PendingInviteCard({ invite, onAccepted }: { invite: PendingInvite; onAccepted: (accepted: { holderId: string; eventId: string }) => void }) {
  const { t } = useLocale()
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)

  const onAccept = async () => {
    const base = myApiBase()
    const session = loadSession()
    if (!base || !session) { setFailed(true); return }
    setBusy(true)
    setFailed(false)
    try {
      const result = await acceptPendingInvite(session.idToken, base, invite.holderId)
      // GUARDIAN CLAIM record — the same post-claim handling as the /claim
      // landing (ClaimLanding): the result's own carry is authoritative, the
      // listed invite's carry is the fallback. Device-local only; a storage
      // failure never undoes the accept.
      const guardianName = result.claimKind === 'guardian'
        ? ((result.childName ?? '').trim() || invite.childName || '')
        : result.claimKind === 'self' ? null : (invite.claimKind === 'guardian' ? (invite.childName ?? '') : null)
      try {
        if (guardianName !== null) recordGuardianClaim({ ticketId: result.ticketId, eventId: result.eventId, childName: guardianName })
        else removeGuardianClaim(result.ticketId)
      } catch { /* device-local record only — the accept stands */ }
      bumpReadinessRefresh()
      onAccepted({ holderId: invite.holderId, eventId: result.eventId || invite.eventId })
    } catch {
      setFailed(true)
      setBusy(false)
    }
  }

  return (
    <div
      data-testid="getready-invite-card"
      data-holder-id={invite.holderId}
      style={{
        background: 'var(--tru-surface-2)',
        border: 'var(--tru-slate-border)',
        borderRadius: 'var(--tru-slate-radius)',
        overflow: 'hidden',
        marginBlockEnd: 'var(--tru-card-gap)',
        boxShadow: 'var(--tru-slate-halo), var(--tru-slate-shadow)',
      }}
    >
      <div style={{ padding: '14px 16px', background: 'var(--tru-slate-header-fill)', borderBlockEnd: 'var(--tru-slate-header-rule)' }}>
        <div className="mono" style={{ fontSize: 9.5, letterSpacing: '.11em', textTransform: 'uppercase', color: 'var(--tru-text-tertiary)', marginBlockEnd: 3 }}>
          {t('getReady.invite.title')}
        </div>
        <div style={{ fontSize: 14.5, fontWeight: 800, lineHeight: 1.2 }}>{invite.eventName}</div>
        {invite.ticketTypeName && (
          <div data-testid="getready-invite-tickettype" style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--tru-text-secondary)', marginBlockStart: 2 }}>
            {invite.ticketTypeName}
          </div>
        )}
        {invite.claimKind === 'guardian' && invite.childName && (
          <div data-testid="getready-invite-child" style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--tru-status-teal-bright)', marginBlockStart: 2 }}>
            {t('getReady.invite.forChild', { name: invite.childName })}
          </div>
        )}
        {invite.invitedByName && (
          <div data-testid="getready-invite-by" style={{ fontSize: 11.5, color: 'var(--tru-text-tertiary)', marginBlockStart: 2 }}>
            {t('getReady.invite.invitedBy', { name: invite.invitedByName })}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', flexWrap: 'wrap' }}>
        <button
          type="button"
          data-testid="getready-invite-accept"
          onClick={() => { void onAccept() }}
          disabled={busy}
          style={{
            minHeight: 'var(--tap-min)', borderRadius: 9, padding: '0 16px', border: 0,
            background: 'var(--accent)', color: 'var(--accent-ink)', fontFamily: 'inherit',
            fontSize: 13, fontWeight: 800, cursor: busy ? 'default' : 'pointer',
          }}
        >
          {busy ? t('getReady.invite.accepting') : t('getReady.invite.accept')}
        </button>
        {failed && (
          <p data-testid="getready-invite-error" role="alert" style={{ margin: 0, fontSize: 12.5, color: 'var(--tru-text-secondary)' }}>
            {t('getReady.invite.error')}
          </p>
        )}
      </div>
    </div>
  )
}
